import {
  showSuccess,
  showConfirm,
  showCustom,
} from "@/assets/js/utils/alertService";
import api from "@/assets/js/utils/api.js";

export default {
  name: "CategoryManagement",

  data() {
    return {
      searchQuery: "",
      activeFilter: "all",
      showModal: false,
      editingCategory: null,
      preSelectedParent: null,
      expandedIds: [],
      formData: {
        type: "parent",
        parentId: "",
        name: "",
        status: "active",
      },
      filters: [],
      categories: [],
      currentPage: 1,      // 目前頁數
    pageSize: 5,         // 一頁 6 筆 (可依需求調整)
    };
  },

  computed: {
filteredCategories() {
  let result = this.categories;

  // 🔍 搜尋邏輯
  if (this.searchQuery) {
    const query = this.searchQuery.toLowerCase();
    const expanded = []; // 暫存要展開的母類別 ID

    result = result
      .filter((parent) => {
        const parentMatch = parent.name.toLowerCase().includes(query);
        const childMatch = parent.children.some((child) =>
          child.name.toLowerCase().includes(query)
        );

        // ✅ 若有子類別符合搜尋條件 → 自動展開該母類別
        if (childMatch) expanded.push(parent.id);

        return parentMatch || childMatch;
      })
      .map((parent) => {
        if (!parent.name.toLowerCase().includes(query)) {
          return {
            ...parent,
            children: parent.children.filter((child) =>
              child.name.toLowerCase().includes(query)
            ),
          };
        }
        return parent;
      });

    // ✅ 更新展開母類別列表（確保畫面展開）
    this.expandedIds = expanded;
  } else {
    // 🧹 若清除搜尋字串則收起所有展開
    this.expandedIds = [];
  }

  // 🧭 狀態篩選邏輯
  switch (this.activeFilter) {
    case "parent":
      // ✅ 僅顯示母類別
      result = result.filter((cat) => cat.type === "parent");
      break;

    case "child":
      // ✅ 直接平面化顯示所有子類別
      result = result.flatMap((parent) =>
        parent.children.map((child) => ({
          ...child,
          parentName: parent.name, // 額外附上母類別名稱方便顯示
        }))
      );
      break;

    case "active":
      result = result
        .filter(
          (cat) =>
            cat.status === "active" ||
            cat.children.some((c) => c.status === "active")
        )
        .map((cat) => ({
          ...cat,
          children: cat.children.filter((c) => c.status === "active"),
        }));
      break;

    case "inactive":
      result = result
        .filter(
          (cat) =>
            cat.status === "inactive" ||
            cat.children.some((c) => c.status === "inactive")
        )
        .map((cat) => ({
          ...cat,
          children: cat.children.filter((c) => c.status === "inactive"),
        }));
      break;

    case "all":
    default:
      result = result.map((cat) => ({
        ...cat,
        children: [...cat.children],
      }));
      break;
  }

  return result;
},


    activeParentCategories() {
      // ✅ 改為回傳所有母類別，不限 active 狀態
      return this.categories.filter((cat) => cat.type === "parent");
    },

    stats() {
      const parentCount = this.categories.filter(
        (c) => c.type === "parent"
      ).length;
      const childCount = this.categories.reduce(
        (sum, cat) => sum + cat.children.length,
        0
      );
      const total = parentCount + childCount;

      let activeCount = 0;
      this.categories.forEach((parent) => {
        if (parent.status === "active") activeCount++;
        parent.children.forEach((child) => {
          if (child.status === "active") activeCount++;
        });
      });

      return {
        parentCount,
        childCount,
        total,
        active: activeCount,
      };
    },

    // ===========================
    // 🧮 自動統計分類數量
    // ===========================
    dynamicFilters() {
      const parentCount = this.categories.filter(
        (c) => c.type === "parent"
      ).length;
      const childCount = this.categories.reduce(
        (sum, cat) => sum + cat.children.length,
        0
      );
      const total = parentCount + childCount;

      const activeCount = this.categories.reduce((sum, parent) => {
        let count = parent.status === "active" ? 1 : 0;
        count += parent.children.filter((c) => c.status === "active").length;
        return sum + count;
      }, 0);

      const inactiveCount = total - activeCount;

      return [
        { label: "全部類別", value: "all", count: total },
        { label: "母類別", value: "parent", count: parentCount },
        { label: "子類別", value: "child", count: childCount },
        { label: "啟用中", value: "active", count: activeCount },
        { label: "已停用", value: "inactive", count: inactiveCount },
      ];
    },

        // ✅ 分頁後的資料 (電腦版表格用)
  pagedCategories() {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredCategories.slice(start, start + this.pageSize);
  },
  
  // ✅ 總頁數
  totalPages() {
    return Math.ceil(this.filteredCategories.length / this.pageSize);
  },
  },

  mounted() {
    this.fetchCategories();
  // ✅ 預設不展開任何母類別（保持收合）
  this.expandedIds = [];
  },

  watch: {
  // ✅ 當搜尋關鍵字變更 → 回第一頁
  searchQuery() {
    this.currentPage = 1;
  },
  
  // ✅ 當篩選條件變更 → 檢查頁碼是否合法
  activeFilter() {
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages || 1;
    }
  },
  
  // ✅ 避免任何篩選導致頁碼落空
  filteredCategories() {
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages || 1;
    }
  },
  },

  methods: {
    handleSearch() {
      // 搜尋邏輯由 computed 處理
    },

    // 🟢 編輯母類別或子類別（PUT 到後端）
    async updateCategory(category) {
      try {
        // ✅ 先重排整個畫面的 Sequence
        this.recalculateSequences();

        let endpoint = "";
        let payload = {};

        if (category.type === "parent") {
          endpoint = `/category/main/update`;
          payload = {
            Id: category.id,
            CategoryName: this.formData.name,
            Description: this.formData.description || "",
            IsActive: this.formData.status === "active",
            Sequence: category.sequence, // ✅ 現在已經是正確的新排序
            Delete: false,
          };
        } else {
          endpoint = `/category/sub/update`;
          payload = {
            Id: category.id,
            SubCategoryName: this.formData.name,
            MainCategoryId: category.parentId,
            Description: this.formData.description || "",
            IsActive: this.formData.status === "active",
            Sequence: category.sequence, // ✅ 子類別也依 UI 順序更新
            Delete: false,
          };
        }

        // 🔥 呼叫後端更新 API
        const { data: result } = await api.put(endpoint, payload);

        if (result.success) {
          showSuccess("類別更新成功！");
          await this.fetchCategories(); // 重新載入畫面資料
          this.recalculateSequences(); // ✅ 確保畫面順序正確
        } else {
          showCustom({
            icon: "error",
            title: "更新失敗",
            text: result.message || "伺服器未回傳成功狀態。",
          });
        }
      } catch (error) {
        if (error.response?.status === 401) return; // 401 交給全域攔截器處理
        showCustom({
          icon: "error",
          title: "伺服器錯誤",
          text: error.response?.data?.message || error.message,
        });
      }
    },

    recalculateSequences() {
      // 母類別依照畫面順序重新排序
      this.categories.forEach((parent, parentIndex) => {
        parent.sequence = parentIndex + 1; // 由上到下重新給 Sequence

        // 子類別也重新排序（若有）
        if (Array.isArray(parent.children) && parent.children.length > 0) {
          parent.children.forEach((child, childIndex) => {
            child.sequence = childIndex + 1; // 子類別同樣給予序號
          });
        }
      });
    },

    // 📦 從後端取得所有母類別與子類別
    async fetchCategories() {
      try {
        // 🔥 呼叫後端 API
        const { data: result } = await api.get("/category/all");

        // ✅ 驗證資料格式
        if (result.success && Array.isArray(result.data)) {
          // 轉換後端資料為前端可用格式
          this.categories = result.data.map((parent) => ({
            id: parent.MainCategoryId, // 對應 MainCategoryId
            name: parent.MainCategoryName, // 對應 MainCategoryName
            description: parent.Description, // 對應 Description
            type: "parent", // 類型
            status: parent.IsActive ? "active" : "inactive", // 狀態轉換
            createdAt: new Date().toISOString().split("T")[0], // 可用假日期（若後端沒回傳）
            children: (parent.SubCategories || []).map((child) => ({
              id: child.SubCategoryId, // 對應 SubCategoryId
              name: child.SubCategoryName, // 對應 SubCategoryName
              description: child.Description, // 子類別描述
              parentId: parent.MainCategoryId, // 對應母類別ID
              type: "child", // 類型
              status: child.IsActive ? "active" : "inactive", // 狀態轉換
              createdAt: new Date().toISOString().split("T")[0],
            })),
          }));

          // 預設展開所有母類別
          this.expandedIds = [];
        } else {
          showCustom({
            icon: "error",
            title: "載入失敗",
            text: result.message || "伺服器未回傳有效資料。",
          });
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          return;
        }
        // 🧨 捕捉例外
        showCustom({
          icon: "error",
          title: "伺服器錯誤",
          text: error.response?.data?.message || error.message,
        });
      }
    },

    // 📦 從後端取得所有母類別與子類別

    toggleExpand(parentId) {
      const index = this.expandedIds.indexOf(parentId);
      if (index > -1) {
        this.expandedIds.splice(index, 1);
      } else {
        this.expandedIds.push(parentId);
      }
    },

    openAddModal() {
      this.editingCategory = null;
      this.preSelectedParent = null;
      this.formData = {
        type: "parent",
        parentId: "",
        name: "",
        status: "active",
      };
      this.showModal = true;
    },

    openAddChildModal(parent) {
      this.editingCategory = null;
      this.preSelectedParent = parent;
      this.formData = {
        type: "child",
        parentId: parent.id,
        name: "",
        status: "active",
      };
      this.showModal = true;
    },

    editCategory(category, parent = null) {
      this.editingCategory = category;
      this.preSelectedParent = parent;
      this.formData = {
        type: category.type,
        parentId: category.parentId || "",
        name: category.name,
        status: category.status,
      };
      this.showModal = true;
    },

    handleTypeChange() {
      this.formData.parentId = "";
    },

    // ===========================
    // 🗑️ 真實刪除母類別／子類別
    // ===========================
    async deleteCategory(category = null) {
      if (!category) return;

      // 檢查母類別是否有子類別
      if (category.type === "parent" && category.children.length > 0) {
        showCustom({
          icon: "warning",
          title: "無法刪除",
          text: `此母類別下有 ${category.children.length} 個子類別，請先刪除所有子類別。`,
        });
        return;
      }

      const typeName = category.type === "parent" ? "母類別" : "子類別";
      const confirmed = await showConfirm(
        `確定要刪除${typeName}「${category.name}」嗎？\n\n此操作無法復原！`
      );

      if (!confirmed.isConfirmed) return;

      try {
        let endpoint = "";
        // ✅ 根據類別類型選擇不同 API
        if (category.type === "parent") {
          endpoint = `/category/main/${category.id}`; // 刪除母類別
        } else {
          endpoint = `/category/sub/${category.id}`; // 刪除子類別
        }

        // 🔥 呼叫後端 API
        const { data: result } = await api.delete(endpoint);

        if (result.success) {
          // 前端同步刪除畫面資料
          if (category.type === "parent") {
            this.categories = this.categories.filter(
              (c) => c.id !== category.id
            );
          } else {
            const parent = this.categories.find(
              (c) => c.id === category.parentId
            );
            if (parent) {
              parent.children = parent.children.filter(
                (c) => c.id !== category.id
              );
            }
          }

          showSuccess(`${typeName}已成功刪除！`);
        } else {
          showCustom({
            icon: "error",
            title: "刪除失敗",
            text: result.message || "伺服器未回傳成功狀態。",
          });
        }
      } catch (error) {
        showCustom({
          icon: "error",
          title: "伺服器錯誤",
          text: error.response?.data?.message || error.message,
        });
      }
    },

    async saveCategory() {
      // ========================
      // 🟡 編輯模式：呼叫後端更新
      // ========================
      if (this.editingCategory) {
        await this.updateCategory(this.editingCategory); // 呼叫更新 API
        this.closeModal(); // 關閉 Modal
        return;
      }

      // ========================
      // 🟢 新增模式
      // ========================
      if (this.formData.type === "parent") {
        try {
          const { data: result } = await api.post("/category/main/add", {
            categoryName: this.formData.name,
            description: this.formData.description || "",
            isactive: this.formData.status === "active",
          });

          if (result.success) {
            this.categories.push({
              id: result.categoryId,
              name: this.formData.name,
              type: "parent",
              status: this.formData.status,
              createdAt: new Date().toISOString().split("T")[0],
              children: [],
            });
            showSuccess("母類別新增成功！");
          } else {
            showCustom({
              icon: "error",
              title: "新增失敗",
              text: result.message || "請稍後再試。",
            });
          }
        } catch (error) {
          showCustom({
            icon: "error",
            title: "伺服器錯誤",
            text: error.message || "無法連線伺服器。",
          });
        }
      } else {
        try {
          const { data: result } = await api.post("/category/sub/add", {
            subCategoryName: this.formData.name,
            mainCategoryId: this.formData.parentId,
            description: this.formData.description || "",
            isActive: this.formData.status === "active",
          });

          if (result.success) {
            const parent = this.categories.find(
              (c) => c.id === this.formData.parentId
            );
            if (parent) {
              parent.children.push({
                id: result.subCategoryId,
                name: this.formData.name,
                type: "child",
                parentId: this.formData.parentId,
                status: this.formData.status,
                createdAt: new Date().toISOString().split("T")[0],
              });

              if (!this.expandedIds.includes(parent.id)) {
                this.expandedIds.push(parent.id);
              }
            }

            showSuccess("子類別新增成功！");
          } else {
            showCustom({
              icon: "error",
              title: "新增失敗",
              text: result.message || "請稍後再試。",
            });
          }
        } catch (error) {
          showCustom({
            icon: "error",
            title: "伺服器錯誤",
            text: error.message || "無法連線伺服器。",
          });
        }
      }

      this.closeModal();
    },

    closeModal() {
      this.showModal = false;
      this.editingCategory = null;
      this.preSelectedParent = null;
      this.formData = {
        type: "parent",
        parentId: "",
        name: "",
        status: "active",
      };
    },

    getModalTitle() {
      if (this.editingCategory) {
        return this.editingCategory.type === "parent"
          ? "編輯母類別"
          : "編輯子類別";
      }
      if (this.preSelectedParent) {
        return "新增子類別";
      }
      return "新增類別";
    },

  // ✅ 下一頁
  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  },
  
  // ✅ 上一頁
  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  },
  },
};
